import React, { useState, useEffect, useContext } from 'react';
import { Users, Building, Shield, Settings, Server } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import apiService from '../../utils/api';
import { AuthContext } from '../../App';
import { Navigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';

function AdminDashboard() {
  const toast = useToast();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    phone_number: ''
  });
  const { user, hasRole } = useContext(AuthContext);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const res = await apiService.request('/admin/dashboard-stats');

        if (res.data) {
            setStats(res.data);
        } else {
            setError(res.error || 'Failed to load stats');
        }
      } catch (err) {
        console.error('Error fetching admin stats:', err);
        setError('Failed to load system statistics');
      } finally {
        setLoading(false);
      }
    };

    if (hasRole('admin')) {
        fetchStats();
    }
  }, [hasRole]);

  if (!hasRole('admin')) {
    return <Navigate to="/home" replace />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddOwner = async (e) => {
    e.preventDefault();
    setAddingUser(true);
    setError(null);

    try {
      const res = await apiService.request('/admin/owners', {
        method: 'POST',
        body: formData
      });

      if (res.data) {
        setShowAddModal(false);
        setFormData({ name: '', username: '', password: '', phone_number: '' });
        // Optionally refresh stats here
        setStats(prev => ({
          ...prev,
          total_owners: (prev?.total_owners || 0) + 1
        }));
        toast.success('Owner created successfully!');
      } else {
        toast.error(res.error || res.message || 'Failed to create owner');
      }
    } catch (err) {
      console.error('Error creating owner:', err);
      toast.error(err.message || 'Failed to create owner');
    } finally {
      setAddingUser(false);
    }
  };

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">
          {content}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return renderLayout(
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading God Mode Overview...</p>
      </div>
    );
  }

  if (error) {
    return renderLayout(
      <div className="error-container">
        <h3>Error loading secure view</h3>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return renderLayout(
    <>
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Shield size={28} color="var(--primary)" />
          System Overview (God Mode)
        </h1>
        <div className="header-actions">
           <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
             <Users size={18} />
             Add New Owner
           </button>
           <span className="role-badge" style={{ background: '#ff4d4f', color: 'white', marginLeft: '10px' }}>SUPER ADMIN</span>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
            <Users size={24} color="#3b82f6" />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats?.total_owners || 0}</h3>
            <p className="stat-label">Total Owners</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
            <Users size={24} color="#10b981" />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats?.total_employees || 0}</h3>
            <p className="stat-label">Total Employees</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
            <Building size={24} color="#f59e0b" />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats?.total_tabelas || 0}</h3>
            <p className="stat-label">Active Tabelas</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
            <Server size={24} color="#ef4444" />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats?.total_animals || 0}</h3>
            <p className="stat-label">Total Registered Animals</p>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '2rem' }}>
        <h2 style={{ marginBottom: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
          God Mode Access Info
        </h2>
        <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
          As a Super Admin, you have unrestricted view access to all data across all tables, khatas, customers, and financial records. 
          Use the navigation sidebar to explore data as if you were an owner, but seeing everything system-wide.
        </p>
        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
           <button className="btn btn-primary" onClick={() => window.location.href='/employees'}>
             Manage All Users
           </button>
           <button className="btn btn-secondary" onClick={() => window.location.href='/tabela'}>
             View All Tabelas
           </button>
        </div>
      </div>

      {/* Add Owner Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Create New Business Owner</h3>
              <button 
                className="close-btn"
                onClick={() => setShowAddModal(false)}
              >
                &times;
              </button>
            </div>
            
            <form onSubmit={handleAddOwner} className="modal-form">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  name="username"
                  className="form-input"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  name="password"
                  className="form-input"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input
                  type="text"
                  name="phone_number"
                  className="form-input"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowAddModal(false)}
                  disabled={addingUser}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={addingUser}
                >
                  {addingUser ? 'Creating...' : 'Create Owner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminDashboard;
